import type {
	Crew,
	InformationRetrievalPickers,
	ProducedCombination,
	ProductSize,
	ProductType,
} from "../models/InformationRetrieval";

export const ALL_SHIFTS = "0";
export const CURRENT_SHIFT = "-";

export interface FilterSelection {
	shiftId: string;
	productTypeId: number | null;
	productSizeId: number | null;
}

export interface FilterOptions {
	crews: Crew[];
	canUseCurrentShift: boolean;
	productTypes: ProductType[];
	productSizes: ProductSize[];
	/** False when nothing with an active type and product ran in the range. */
	hasProduction: boolean;
}

const EMPTY: FilterOptions = {
	crews: [],
	canUseCurrentShift: false,
	productTypes: [],
	productSizes: [],
	hasProduction: false,
};

const resolveCrew = (
	pickers: InformationRetrievalPickers,
	shiftId: string,
): string | null =>
	shiftId === ALL_SHIFTS
		? null
		: shiftId === CURRENT_SHIFT
			? pickers.currentCrewId
			: shiftId;

const matches = (
	c: ProducedCombination,
	crewId: string | null,
	productTypeId: number | null,
	productSizeId: number | null,
) =>
	(crewId == null || c.CrewId === crewId) &&
	(productTypeId == null || c.ProductTypeId === productTypeId) &&
	(productSizeId == null || c.ProductSizeId === productSizeId);

/** Combinations whose type and product the dropdowns can offer (active ones). */
const usableCombinations = (pickers: InformationRetrievalPickers) => {
	const typeIds = new Set(pickers.productTypes.map((t) => t.Id));
	const sizeIds = new Set(pickers.productSizes.map((s) => s.Id));
	return pickers.combinations.filter(
		(c) =>
			c.ProductTypeId != null &&
			c.ProductSizeId != null &&
			typeIds.has(c.ProductTypeId) &&
			sizeIds.has(c.ProductSizeId),
	);
};

/**
 * Each filter offers only the values that ran together with what the OTHER
 * filters hold, whatever order they were picked in: with a product chosen,
 * Shift lists only the crews that rolled it. Matching uses the billet's own
 * crew, type and product, the same columns the report filters on, so any
 * selection built from these lists returns billets.
 */
export const getFilterOptions = (
	pickers: InformationRetrievalPickers | undefined,
	selection: FilterSelection,
): FilterOptions => {
	if (!pickers) return EMPTY;

	const usable = usableCombinations(pickers);
	const crewId = resolveCrew(pickers, selection.shiftId);
	const { productTypeId, productSizeId } = selection;

	const crewIds = new Set(
		usable
			.filter((c) => matches(c, null, productTypeId, productSizeId))
			.map((c) => c.CrewId),
	);
	const typeIds = new Set(
		usable
			.filter((c) => matches(c, crewId, null, productSizeId))
			.map((c) => c.ProductTypeId),
	);
	const sizeIds = new Set(
		usable
			.filter((c) => matches(c, crewId, productTypeId, null))
			.map((c) => c.ProductSizeId),
	);

	return {
		crews: pickers.crews.filter((c) => crewIds.has(c.Id)),
		canUseCurrentShift:
			pickers.currentCrewId != null && crewIds.has(pickers.currentCrewId),
		productTypes: pickers.productTypes.filter((t) => typeIds.has(t.Id)),
		productSizes: pickers.productSizes.filter((s) => sizeIds.has(s.Id)),
		hasProduction: usable.length > 0,
	};
};

export type DroppedFilter = "product" | "productType" | "shift";

/**
 * The filter to reset when the selection no longer ran together in the range,
 * which only a date change can cause. The product is what the report is about,
 * so it is kept longest: first the product goes if it did not run at all, then
 * the type, and last the shift falls back to All shifts. One filter per call.
 */
export const filterToDrop = (
	pickers: InformationRetrievalPickers,
	selection: FilterSelection,
): DroppedFilter | null => {
	const usable = usableCombinations(pickers);
	const { shiftId, productTypeId, productSizeId } = selection;

	if (
		productSizeId != null &&
		!usable.some((c) => matches(c, null, null, productSizeId))
	) {
		return "product";
	}
	if (
		productTypeId != null &&
		!usable.some((c) => matches(c, null, productTypeId, productSizeId))
	) {
		return "productType";
	}
	if (shiftId !== ALL_SHIFTS) {
		const crewId = resolveCrew(pickers, shiftId);
		// The crew belongs to the shift, not to the product, so a combination that carries no
		// product still proves the crew ran. Requiring one here dropped every crew on the
		// straightener screen, where the PLC detects the product for two of the five families
		// and the runs come back without it.
		const crewCombinations = pickers.combinations.filter(
			(c) => c.CrewId != null,
		);
		if (
			crewId == null ||
			!crewCombinations.some((c) =>
				matches(c, crewId, productTypeId, productSizeId),
			)
		) {
			return "shift";
		}
	}
	return null;
};
